import React, { useCallback, useMemo, useState } from 'react';
import { Spin, Pagination, List, Modal, Checkbox, Tag } from 'antd';
import { SendOutlined, FileOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import StatusBadge from '@/components/ui/StatusBadge';
import Markdown from '@/components/ui/Markdown';
import PhaseDescriptionAttachments from '@/components/module/projects/milestone_tab/PhaseDescriptionAttachments';
import { useProjectBacklogs, getBacklogPromotableSprintTasksFromItem, backlogItemAllTasksAreBacklogged } from '@hooks/projects/useProjectBacklogs';
import TaskDetailModal from '@/components/module/projects/task/TaskDetailModal';
import { formatDate, getMilestoneDisplayLabel, getTaskDescriptionMarkdown, isTaskOnDevBacklog } from '@hooks/projects/projectHelpers';
import AppModal from '../AppModal';
import {
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    DetailPanelLoading,
    MutedText,
    CardWithMinHeight,
    ContentCardTitle,
    PanelDivider,
    PanelSectionLabel,
    CreateRoleModalBody,
    CreateRoleModalFooter,
    BoardPageTitle,
    BoardPageSubtitle,
} from '../style';

export default function ProjectBacklogsTab({ projectId }) {
    const {
        backlogs,
        total,
        page,
        limit,
        totalPages,
        isLoading,
        onPageChange,
        canPromoteBacklogs,
        promoteBacklogs,
        isPromotingBacklogs,
    } = useProjectBacklogs(projectId);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedBacklog, setSelectedBacklog] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);
    const [promotePickerOpen, setPromotePickerOpen] = useState(false);
    const [promoteSelectedIds, setPromoteSelectedIds] = useState([]);

    const openDetail = useCallback((item) => {
        if (!item) return;
        setSelectedBacklog(item);
        setDetailOpen(true);
    }, []);

    const closeDetail = useCallback(() => {
        setDetailOpen(false);
        setSelectedBacklog(null);
        setPromotePickerOpen(false);
        setPromoteSelectedIds([]);
    }, []);

    const openPromotePicker = useCallback(() => {
        setPromoteSelectedIds([]);
        setPromotePickerOpen(true);
    }, []);

    const closePromotePicker = useCallback(() => {
        if (isPromotingBacklogs) return;
        setPromotePickerOpen(false);
        setPromoteSelectedIds([]);
    }, [isPromotingBacklogs]);

    const promotableRows = useMemo(() => getBacklogPromotableSprintTasksFromItem(selectedBacklog), [selectedBacklog]);
    const allSprintTasksAreBacklogged = useMemo(() => backlogItemAllTasksAreBacklogged(selectedBacklog), [selectedBacklog]);

    const confirmPromoteBacklogs = useCallback(async () => {
        if (!promoteSelectedIds.length) return;
        try {
            await promoteBacklogs(promoteSelectedIds);
            setPromotePickerOpen(false);
            setPromoteSelectedIds([]);
            closeDetail();
        } catch {
            // Error toast is handled in useProjectBacklogs mutation onError.
        }
    }, [promoteBacklogs, promoteSelectedIds, closeDetail]);

    const closeTaskModal = useCallback(() => {
        setSelectedTask(null);
    }, []);

    const selectedDetail = useMemo(() => {
        const item = selectedBacklog;
        if (!item) return null;
        const title = getMilestoneDisplayLabel(item?.milestone) || 'Backlog';
        const requestedAt = item?.requestedAt ?? item?.createdAt;
        const requestedBy = item?.requestedByName ?? '';
        const clientNote = item?.clientNote?.markdown ?? item?.clientNote ?? '';
        const requestNote = item?.requestNotes?.markdown ?? item?.requestNotes ?? '';
        const requestAttachments = Array.isArray(item?.requestAttachments) ? item.requestAttachments : [];
        const clientAttachments = Array.isArray(item?.clientAttachments) ? item.clientAttachments : [];
        const status = item?.status ?? null;
        return {
            title,
            requestedAt,
            requestedBy,
            clientNote,
            requestNote,
            requestAttachments,
            clientAttachments,
            status,
            phase: item?.phase ?? null,
            milestone: item?.milestone ?? null,
            submissionId: item?.submissionId ?? null,
            submissionType: item?.submissionType ?? null,
            clientRespondedAt: item?.clientRespondedAt ?? null,
            clientResponse: item?.clientResponse ?? null,
        };
    }, [selectedBacklog]);

    const taskDescriptionMarkdown = useMemo(() => {
        const task = selectedTask?.task;
        if (!task) return '';
        return getTaskDescriptionMarkdown(task) || '';
    }, [selectedTask]);

    if (!projectId) {
        return (
            <EmptyStateBlock>
                <DetailPanelLoading>
                    <Spin size="large" />
                </DetailPanelLoading>
            </EmptyStateBlock>
        );
    }

    if (isLoading) {
        return (
            <EmptyStateBlock>
                <DetailPanelLoading>
                    <Spin size="large" />
                </DetailPanelLoading>
            </EmptyStateBlock>
        );
    }

    if (!backlogs || backlogs.length === 0) {
        return (
            <EmptyStateBlock>
                <EmptyStateIcon>
                    <SendOutlined />
                </EmptyStateIcon>
                <EmptyStateTitle>No backlogs yet</EmptyStateTitle>
                <EmptyStateDescription>
                    Items the client rejects to backlog will appear here, along with notes and attachments.
                </EmptyStateDescription>
            </EmptyStateBlock>
        );
    }

    return (
        <>
        <Container $display="flex" $flexDirection="column" $gap="16px" $minHeight="0" $flex="1">
            <Container $display="flex" $flexDirection="column" $gap="4px" style={{ padding: '0 12px' }}>
                <BoardPageTitle>Backlogs</BoardPageTitle>
                <BoardPageSubtitle>Client backlogged items, notes, and the submitted work details.</BoardPageSubtitle>
            </Container>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 12 }}>
                <List
                    dataSource={backlogs}
                    grid={{
                        gutter: 16,
                        xs: 1,
                        sm: 1,
                        md: 2,
                        lg: 2,
                        xl: 3,
                        xxl: 3,
                    }}
                    renderItem={(item) => {
                        const title = getMilestoneDisplayLabel(item?.milestone) || 'Backlog';
                        const requestedAt = item?.requestedAt ?? item?.createdAt;
                        const requestedBy = item?.requestedByName ?? '';
                        const clientNote = item?.clientNote?.markdown ?? item?.clientNote ?? '';
                        const attachments = Array.isArray(item?.clientAttachments) && item.clientAttachments.length > 0
                            ? item.clientAttachments
                            : (Array.isArray(item?.requestAttachments) ? item.requestAttachments : []);
                        const cardSprintTaskRows = getBacklogPromotableSprintTasksFromItem(item);
                        const returnToDevRows = cardSprintTaskRows.filter((r) => r.isBacklog);

                        return (
                            <List.Item key={item?.id ?? Math.random()} style={{ margin: 0, padding: '0 0 16px 0' }}>
                                <CardWithMinHeight
                                    role="button"
                                    tabIndex={0}
                                    $padding="16px"
                                    $minHeight="180px"
                                    $noHoverShadow
                                    onClick={() => openDetail(item)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            openDetail(item);
                                        }
                                    }}
                                    style={{ height: 350, maxHeight: 350, overflow: 'hidden', cursor: 'pointer' }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                            gap: 8,
                                            marginBottom: 8,
                                        }}
                                    >
                                        <ContentCardTitle $marginBottom="0" style={{ flex: 1, minWidth: 0 }}>
                                            {title}
                                        </ContentCardTitle>
                                        {item?.status != null && <StatusBadge status={item.status} size="compact" />}
                                    </div>

                                    <PanelDivider />

                                    <div onClick={(e) => e.stopPropagation()} style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
                                        <MutedText style={{ fontSize: 12 }}>
                                            Backlogged {requestedAt ? `· ${formatDate(requestedAt)}` : ''}
                                        </MutedText>
                                        {requestedBy ? (
                                            <>
                                                <PanelDivider />
                                                <MutedText style={{ fontSize: 12 }}>Requested by: {requestedBy}</MutedText>
                                            </>
                                        ) : null}

                                        {returnToDevRows.length > 0 ? (
                                            <>
                                                <PanelDivider />
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                    {returnToDevRows.map((r) => (
                                                        <div
                                                            key={r.sprintTaskId}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                gap: 8,
                                                                flexWrap: 'wrap',
                                                            }}
                                                        >
                                                            <MutedText style={{ fontSize: 12, minWidth: 0 }}>{r.displayLabel}</MutedText>
                                                            <Tag color="orange" style={{ margin: 0 }}>
                                                                Return to dev
                                                            </Tag>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        ) : null}

                                        {(String(clientNote || '').trim() || attachments.length > 0) && (
                                            <>
                                                <PanelDivider />
                                                <div
                                                    style={{
                                                        border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                        borderRadius: 10,
                                                        padding: '10px 10px 8px',
                                                        background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                                                        <div style={{ minWidth: 0, flex: 1 }}>
                                                            <div
                                                                style={{
                                                                    fontSize: 11,
                                                                    fontWeight: 700,
                                                                    letterSpacing: '0.04em',
                                                                    textTransform: 'uppercase',
                                                                    color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                                                    marginBottom: 6,
                                                                    fontFamily: 'var(--font-family-geist)',
                                                                }}
                                                            >
                                                                Client notes
                                                            </div>
                                                            {String(clientNote || '').trim() ? (
                                                                <div style={{ height: 72, overflow: 'hidden' }}>
                                                                    <Markdown
                                                                        source={String(clientNote || '').trim()}
                                                                        variant="compact"
                                                                        lineClamp={3}
                                                                        linkPrimary
                                                                        richInlineAnchors
                                                                        attachmentItems={attachments}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div style={{ fontSize: 13, color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))' }}>—</div>
                                                            )}
                                                        </div>
                                                        {attachments.length > 0 ? (
                                                            <div style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                                                <PhaseDescriptionAttachments items={attachments} style={{ marginTop: 0 }} />
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </CardWithMinHeight>
                            </List.Item>
                        );
                    }}
                />
            </div>
            {totalPages > 1 && (
                <Container $display="flex" $justifyContent="flex-end" $flexShrink="0">
                    <Pagination
                        current={page}
                        pageSize={limit}
                        total={total}
                        onChange={onPageChange}
                        showSizeChanger
                        showTotal={(t, range) => `${range[0]}-${range[1]} of ${t}`}
                    />
                </Container>
            )}
        </Container>
            <AppModal
                size="lg"
                title={selectedDetail?.title ?? 'Backlog details'}
                open={detailOpen}
                onCancel={closeDetail}
                footer={null}
            >
                <CreateRoleModalBody>
                    <Container $display="flex" $flexDirection="column" $gap="10px">
                        {selectedDetail?.status != null && (
                            <Container $display="flex" $justifyContent="flex-end">
                                <StatusBadge status={selectedDetail.status} size="compact" />
                            </Container>
                        )}
                        <MutedText style={{ fontSize: 12 }}>
                            Backlogged {selectedDetail?.requestedAt ? `· ${formatDate(selectedDetail.requestedAt)}` : ''}
                        </MutedText>
                        {selectedDetail?.requestedBy ? (
                            <MutedText style={{ fontSize: 12 }}>
                                Requested by: {selectedDetail.requestedBy}
                            </MutedText>
                        ) : null}
                        {selectedDetail?.clientRespondedAt ? (
                            <MutedText style={{ fontSize: 12 }}>
                                Client responded {`· ${formatDate(selectedDetail.clientRespondedAt)}`}
                            </MutedText>
                        ) : null}
                        {selectedDetail?.clientResponse ? (
                            <MutedText style={{ fontSize: 12 }}>
                                Client response: {String(selectedDetail.clientResponse)}
                            </MutedText>
                        ) : null}

                        <PanelDivider />

                        <Container>
                            <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                <FileOutlined />
                                Client notes
                            </PanelSectionLabel>
                            {String(selectedDetail?.clientNote || '').trim() ? (
                                <div style={{ height: 72, overflow: 'hidden' }}>
                                    <Markdown
                                        source={String(selectedDetail.clientNote).trim()}
                                        variant="compact"
                                        lineClamp={3}
                                        linkPrimary
                                        richInlineAnchors
                                        attachmentItems={selectedDetail.clientAttachments}
                                    />
                                </div>
                            ) : (
                                <MutedText style={{ fontSize: 12 }}>—</MutedText>
                            )}
                        </Container>

                        {Array.isArray(selectedDetail?.clientAttachments) && selectedDetail.clientAttachments.length > 0 ? (
                            <>
                                <PanelDivider />
                                <Container $display="flex" $justifyContent="space-between" $alignItems="center" $gap="10px">
                                    <PanelSectionLabel style={{ margin: 0 }}>Attachments</PanelSectionLabel>
                                    <div>
                                        <PhaseDescriptionAttachments items={selectedDetail.clientAttachments} style={{ marginTop: 0 }} />
                                    </div>
                                </Container>
                            </>
                        ) : null}

                        {String(selectedDetail?.requestNote || '').trim() || (selectedDetail?.requestAttachments?.length ?? 0) > 0 ? (
                            <>
                                <PanelDivider />
                                <Container>
                                    <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                        <FileOutlined />
                                        Request notes
                                    </PanelSectionLabel>
                                    {String(selectedDetail?.requestNote || '').trim() ? (
                                        <Markdown
                                            source={String(selectedDetail.requestNote).trim()}
                                            variant="compact"
                                            linkPrimary
                                            richInlineAnchors
                                            attachmentItems={selectedDetail.requestAttachments}
                                        />
                                    ) : (
                                        <MutedText style={{ fontSize: 12 }}>—</MutedText>
                                    )}
                                </Container>
                                {Array.isArray(selectedDetail?.requestAttachments) && selectedDetail.requestAttachments.length > 0 ? (
                                    <Container $display="flex" $justifyContent="space-between" $alignItems="center" $gap="10px">
                                        <PanelSectionLabel style={{ margin: 0 }}>Request attachments</PanelSectionLabel>
                                        <div>
                                            <PhaseDescriptionAttachments items={selectedDetail.requestAttachments} style={{ marginTop: 0 }} />
                                        </div>
                                    </Container>
                                ) : null}
                            </>
                        ) : null}

                        {(selectedDetail?.phase || selectedDetail?.milestone) && (
                            <>
                                <PanelDivider />
                                <Container $display="flex" $flexDirection="column" $gap="10px">
                                    {selectedDetail?.phase?.title ? (
                                        <div>
                                            <PanelSectionLabel style={{ marginBottom: 6 }}>Phase</PanelSectionLabel>
                                            <MutedText style={{ fontSize: 13 }}>{selectedDetail.phase.title}</MutedText>
                                        </div>
                                    ) : null}

                                    {selectedDetail?.milestone ? (
                                        <div>
                                            <PanelSectionLabel style={{ marginBottom: 6 }}>Milestone</PanelSectionLabel>
                                            <MutedText style={{ fontSize: 13 }}>
                                                {selectedDetail.milestone.milestoneNumber ? `${selectedDetail.milestone.milestoneNumber} · ` : ''}
                                                {selectedDetail.milestone.name ?? '—'}
                                            </MutedText>
                                            {String(selectedDetail?.milestone?.description?.markdown ?? selectedDetail?.milestone?.description?.content ?? selectedDetail?.milestone?.description ?? '').trim() ? (
                                                <div style={{ marginTop: 8 }}>
                                                    <PanelSectionLabel style={{ marginBottom: 6 }}>Milestone description</PanelSectionLabel>
                                                    <Markdown
                                                        source={String(
                                                            selectedDetail.milestone.description?.markdown ??
                                                            selectedDetail.milestone.description?.content ??
                                                            selectedDetail.milestone.description ??
                                                            '',
                                                        ).trim()}
                                                        variant="compact"
                                                        linkPrimary
                                                        richInlineAnchors
                                                        attachmentItems={Array.isArray(selectedDetail?.milestone?.attachments) ? selectedDetail.milestone.attachments : []}
                                                    />
                                                    {Array.isArray(selectedDetail?.milestone?.attachments) && selectedDetail.milestone.attachments.length > 0 ? (
                                                        <Container $display="flex" $justifyContent="flex-end" style={{ marginTop: 6 }}>
                                                            <PhaseDescriptionAttachments items={selectedDetail.milestone.attachments} style={{ marginTop: 0 }} />
                                                        </Container>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}

                                    {Array.isArray(selectedDetail?.milestone?.sprints) && selectedDetail.milestone.sprints.length > 0 ? (
                                        <div>
                                            <PanelSectionLabel style={{ marginBottom: 8 }}>Sprints & tasks</PanelSectionLabel>
                                            <Container $display="flex" $flexDirection="column" $gap="10px">
                                                {selectedDetail.milestone.sprints.map((sp) => {
                                                    const sprintTitle = `${sp?.sprintNumber ? `${sp.sprintNumber} · ` : ''}${sp?.name ?? '—'}`;
                                                    const tasks = Array.isArray(sp?.tasks) ? sp.tasks : [];
                                                    const sprintDesc =
                                                        sp?.description?.markdown ??
                                                        sp?.description?.content ??
                                                        sp?.description ??
                                                        '';
                                                    const sprintAttachments = Array.isArray(sp?.attachments)
                                                        ? sp.attachments
                                                        : Array.isArray(sp?.description?.attachments)
                                                            ? sp.description.attachments
                                                            : [];
                                                    return (
                                                        <div
                                                            key={sp?.id ?? sprintTitle}
                                                            style={{
                                                                border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                borderRadius: 12,
                                                                padding: 12,
                                                                background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                                                                <div style={{ minWidth: 0 }}>
                                                                    <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-family-geist)' }}>
                                                                        {sprintTitle}
                                                                    </div>
                                                                    <MutedText style={{ fontSize: 12 }}>
                                                                        {sp?.startDate ? formatDate(sp.startDate) : '—'} → {sp?.endDate ? formatDate(sp.endDate) : '—'}
                                                                    </MutedText>
                                                                </div>
                                                                {sp?.status ? <StatusBadge status={sp.status} size="compact" /> : null}
                                                            </div>

                                                            {String(sprintDesc || '').trim() || sprintAttachments.length > 0 ? (
                                                                <>
                                                                    <div style={{ marginBottom: 10 }}>
                                                                        <PanelSectionLabel style={{ marginBottom: 6 }}>Sprint description</PanelSectionLabel>
                                                                        {String(sprintDesc || '').trim() ? (
                                                                            <Markdown
                                                                                source={String(sprintDesc).trim()}
                                                                                variant="compact"
                                                                                lineClamp={4}
                                                                                linkPrimary
                                                                                richInlineAnchors
                                                                                attachmentItems={sprintAttachments}
                                                                            />
                                                                        ) : (
                                                                            <MutedText style={{ fontSize: 12 }}>—</MutedText>
                                                                        )}
                                                                        {sprintAttachments.length > 0 ? (
                                                                            <Container $display="flex" $justifyContent="flex-end" style={{ marginTop: 6 }}>
                                                                                <PhaseDescriptionAttachments items={sprintAttachments} style={{ marginTop: 0 }} />
                                                                            </Container>
                                                                        ) : null}
                                                                    </div>
                                                                </>
                                                            ) : null}

                                                            {tasks.length > 0 ? (
                                                                <Container $display="flex" $flexDirection="column" $gap="8px">
                                                                    {tasks.map((t) => {
                                                                        const tid = t?.taskNumber ?? t?.taskId ?? t?.id ?? '—';
                                                                        const desc = t?.description?.markdown ?? t?.description?.content ?? '';
                                                                        const taskAttachments = Array.isArray(t?.description?.attachments) ? t.description.attachments : [];
                                                                        return (
                                                                            <div
                                                                                key={t?.sprintTaskId ?? t?.taskId ?? tid}
                                                                                role="button"
                                                                                tabIndex={0}
                                                                                onClick={() => setSelectedTask({ sprint: sp, task: t })}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                                        e.preventDefault();
                                                                                        setSelectedTask({ sprint: sp, task: t });
                                                                                    }
                                                                                }}
                                                                                style={{
                                                                                    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                                    borderRadius: 10,
                                                                                    padding: 10,
                                                                                    background: 'var(--ant-color-bg-container, #fff)',
                                                                                    cursor: 'pointer',
                                                                                }}
                                                                            >
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                                                        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                                                                                            {tid} · {t?.title ?? '—'}
                                                                                        </div>
                                                                                        <PanelSectionLabel style={{ marginBottom: 6 }}>Description</PanelSectionLabel>
                                                                                        {String(desc || '').trim() ? (
                                                                                            <Markdown
                                                                                                source={String(desc).trim()}
                                                                                                variant="compact"
                                                                                                lineClamp={4}
                                                                                                linkPrimary
                                                                                                richInlineAnchors
                                                                                                attachmentItems={taskAttachments}
                                                                                            />
                                                                                        ) : (
                                                                                            <MutedText style={{ fontSize: 12 }}>—</MutedText>
                                                                                        )}
                                                                                    </div>
                                                                                    <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                                                                                        {isTaskOnDevBacklog(t) ? (
                                                                                            <Tag color="orange" style={{ margin: 0 }}>
                                                                                                Return to dev
                                                                                            </Tag>
                                                                                        ) : null}
                                                                                        {t?.status ? <StatusBadge status={t.status} size="compact" /> : null}
                                                                                        {taskAttachments.length > 0 ? (
                                                                                            <div onClick={(e) => e.stopPropagation()}>
                                                                                                <PhaseDescriptionAttachments items={taskAttachments} style={{ marginTop: 0 }} />
                                                                                            </div>
                                                                                        ) : null}
                                                                                    </div>
                                                                                </div>
                                                                                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                                                    {t?.type ? <MutedText style={{ fontSize: 12 }}>Type: {t.type}</MutedText> : null}
                                                                                    {t?.priority ? <MutedText style={{ fontSize: 12 }}>Priority: {t.priority}</MutedText> : null}
                                                                                    {t?.submittedAt ? <MutedText style={{ fontSize: 12 }}>Submitted: {formatDate(t.submittedAt)}</MutedText> : null}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </Container>
                                                            ) : (
                                                                <MutedText style={{ fontSize: 12 }}>No tasks on this sprint.</MutedText>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </Container>
                                        </div>
                                    ) : null}
                                </Container>
                            </>
                        )}
                    </Container>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Container $display="flex" $justifyContent="flex-end" $alignItems="center" $gap="10px" $flexWrap="wrap">
                        <Button
                            $primary
                            $size="14px"
                            permissionDenied={!canPromoteBacklogs}
                            disabled={promotableRows.length === 0 || allSprintTasksAreBacklogged}
                            disabledTooltip={
                                promotableRows.length === 0
                                    ? 'No sprint tasks are listed on this backlog. They may still be loading, or this item has no milestone tasks.'
                                    : allSprintTasksAreBacklogged
                                      ? 'All sprint tasks are already on backlog (return to dev); nothing left to submit.'
                                      : undefined
                            }
                            onClick={openPromotePicker}
                        >
                            Submit tasks
                        </Button>
                        <Button $primary={false} $size="14px" onClick={closeDetail}>
                            Close
                        </Button>
                    </Container>
                </CreateRoleModalFooter>
            </AppModal>
            <Modal
                title="Select tasks to submit"
                open={promotePickerOpen}
                onCancel={closePromotePicker}
                zIndex={1200}
                destroyOnClose
                footer={
                    <Container $display="flex" $justifyContent="flex-end" $gap="10px">
                        <Button $primary={false} $size="14px" disabled={isPromotingBacklogs} onClick={closePromotePicker}>
                            Cancel
                        </Button>
                        <Button
                            $primary
                            $size="14px"
                            permissionDenied={!canPromoteBacklogs}
                            disabledTooltip={promoteSelectedIds.length === 0 ? 'Select at least one sprint task.' : undefined}
                            disabled={isPromotingBacklogs || promoteSelectedIds.length === 0}
                            onClick={confirmPromoteBacklogs}
                        >
                            Submit
                        </Button>
                    </Container>
                }
            >
                <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 4 }}>
                    {promotableRows.length === 0 ? (
                        <MutedText style={{ fontSize: 13 }}>No sprint tasks available.</MutedText>
                    ) : (
                        <Checkbox.Group
                            value={promoteSelectedIds}
                            onChange={(ids) => setPromoteSelectedIds(Array.isArray(ids) ? ids.map(String) : [])}
                            style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}
                        >
                            {promotableRows.map((row) => (
                                <label
                                    key={row.sprintTaskId}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 10,
                                        cursor: row.isBacklog ? 'default' : 'pointer',
                                        padding: '8px 10px',
                                        borderRadius: 10,
                                        border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                        background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                        opacity: row.isBacklog ? 0.85 : 1,
                                    }}
                                >
                                    <Checkbox value={row.sprintTaskId} disabled={row.isBacklog} style={{ marginTop: 2 }} />
                                    <span style={{ minWidth: 0, flex: 1 }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 8,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-family-geist)' }}>{row.displayLabel}</div>
                                            {row.isBacklog ? (
                                                <Tag color="default" style={{ margin: 0 }}>
                                                    Already submitted
                                                </Tag>
                                            ) : null}
                                        </div>
                                        <MutedText style={{ fontSize: 12 }}>{row.sprintLabel}</MutedText>
                                    </span>
                                </label>
                            ))}
                        </Checkbox.Group>
                    )}
                </div>
            </Modal>
            <TaskDetailModal
                open={Boolean(selectedTask?.sprint && selectedTask?.task)}
                task={selectedTask?.task ?? null}
                taskDescriptionMarkdown={taskDescriptionMarkdown}
                taskBugs={[]}
                onClose={closeTaskModal}
                readOnlyBugActions
            />
        </>
    );
}

